import { useState, useEffect, useCallback, useMemo } from 'react';
import toastService from '@/utils/toastService';
import * as mantenimientosApiService from '@/services/mantenimientosApiService';
import * as citasApiService from '@/services/citasApiService';
import * as vehiculosApiService from '@/services/vehiculosApiService';
import * as mecanicosApiService from '@/services/mecanicosApiService';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { useSession } from '@clerk/clerk-react';
import PDFGenerator from '@/components/common/PDFGenerator';
import { normalizeDataObject } from '../utils/normalizationUtils';

// Control global para evitar requests duplicados
let globalFinalizarState = {
  loadingCitas: new Map(), // citaId -> Promise
};

export const useFinalizarMantenimiento = (cita, onSuccess) => {
  
  // Normalizar datos de la cita para manejar diferentes formatos (memoizado para evitar loops)
  const normalizedCita = useMemo(() => {
    return cita ? normalizeDataObject(cita, 'cita') : null;
  }, [cita?.id, cita?.ID]);
  
  // Hooks de empresa y sesión
  const { empresaActual } = useSimpleEmpresa();
  const { session } = useSession();
  
  // Estado inicial del formulario
  const [formData, setFormData] = useState({
    mecanicosIds: [],
    kilometrajeActual: normalizedCita?.vehiculo?.kilometraje || normalizedCita?.vehiculo?.kilometrajeActual || '',
    observaciones: '',
    serviciosRealizados: [],
    repuestosUtilizados: [],
    itemsAdicionales: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [servicios, setServicios] = useState([]);
  const [repuestos, setRepuestos] = useState([]);
  const [mecanicos, setMecanicos] = useState([]);
  const [ivaPorcentaje, setIvaPorcentaje] = useState(19); // Valor por defecto, se actualizará desde la BD

  // Cargar datos iniciales
  useEffect(() => {
    const cargarDatosIniciales = async () => {
      try {
        // Cargar servicios de la cita
        const citaId = normalizedCita?.id || normalizedCita?.ID;
        if (citaId) {
          // Evitar requests duplicados para la misma cita
          if (globalFinalizarState.loadingCitas.has(citaId)) {
            console.log('⚠️ [useFinalizarMantenimiento] Request ya en progreso para cita:', citaId);
            try {
              await globalFinalizarState.loadingCitas.get(citaId);
            } catch (error) {
              // Error ya manejado en el request original
            }
            return;
          }

          try {
            const serviciosPromise = citasApiService.getServiciosSeleccionados(citaId);
            globalFinalizarState.loadingCitas.set(citaId, serviciosPromise);
            
            const serviciosCita = await serviciosPromise;
            const serviciosIniciales = (serviciosCita || []).map(servicioCita => {
              // Normalizar datos del servicio de la cita
              const normalizedServicioCita = normalizeDataObject(servicioCita, 'servicioCita');
              
              return {
                servicioId: normalizedServicioCita.servicioId || normalizedServicioCita.ServicioID,
                precio: (normalizedServicioCita.precioEstimado || normalizedServicioCita.PrecioEstimado || '0').toString(),
                observaciones: normalizedServicioCita.observaciones || normalizedServicioCita.Observaciones || '',
                nombre: normalizedServicioCita.servicioNombre || normalizedServicioCita.ServicioNombre || '',
                descripcion: normalizedServicioCita.servicioDescripcion || normalizedServicioCita.ServicioDescripcion || '',
                esServicioCita: true,
                recordatorioActivo: false,
                tipoRecordatorio: 'kilometraje',
                proximoKilometraje: '',
                proximaFecha: '',
                kilometrosAnticipacion: 1000,
                diasAnticipacion: 30,
                descripcionProximoServicio: '',
                mecanicoObservaciones: ''
              };
            });

            setFormData(prev => ({
              ...prev,
              serviciosRealizados: serviciosIniciales,
              kilometrajeActual: normalizedCita.vehiculo?.kilometraje || normalizedCita.vehiculo?.kilometrajeActual || ''
            }));
          } catch (error) {
            console.warn('No se pudieron cargar servicios de la cita:', error);
            // Continuar sin servicios pre-cargados
            setFormData(prev => ({
              ...prev,
              kilometrajeActual: normalizedCita.vehiculo?.kilometraje || normalizedCita.vehiculo?.kilometrajeActual || ''
            }));
          } finally {
            // Limpiar estado global para esta cita
            globalFinalizarState.loadingCitas.delete(citaId);
          }
        }

        // Cargar catálogos usando APIs multi-empresa
        const sucursalId = normalizedCita?.sucursal_id || normalizedCita?.SucursalID;
        
        // Verificar que tenemos empresa actual
        if (!empresaActual?.id) {
          throw new Error('No hay empresa seleccionada');
        }
        
        const [serviciosData, repuestosData, mecanicosData] = await Promise.all([
          mantenimientosApiService.getServiciosDisponibles(sucursalId),
          mantenimientosApiService.getRepuestosDisponibles(sucursalId),
          mecanicosApiService.getMecanicos(sucursalId) // ✅ Filtrar mecánicos por sucursal seleccionada
        ]);

        //console.log('Catálogos cargados - Servicios:', serviciosData?.length, 'Repuestos:', repuestosData?.length, 'Mecánicos:', mecanicosData?.length);
        //console.log('Primer repuesto (verificar precio):', repuestosData?.[0]);

        // Normalizar datos de los catálogos
        const normalizedServicios = serviciosData.map(servicio => normalizeDataObject(servicio, 'servicio'));
        const normalizedRepuestos = repuestosData.map(repuesto => normalizeDataObject(repuesto, 'repuesto'));
        const normalizedMecanicos = mecanicosData.map(mecanico => normalizeDataObject(mecanico, 'mecanico'));

        setServicios(normalizedServicios);
        setRepuestos(normalizedRepuestos);
        setMecanicos(normalizedMecanicos);

        // Cargar configuración de IVA específica de la empresa
        try {
          const configIva = await mantenimientosApiService.getConfiguraciones(['iva_porcentaje'], empresaActual.id);
          if (configIva && configIva.iva_porcentaje) {
            setIvaPorcentaje(parseFloat(configIva.iva_porcentaje) || 19);
          }
        } catch (error) {
          console.warn('Error cargando configuración de IVA específica, usando valor por defecto:', error);
          setIvaPorcentaje(19);
        }
      } catch (error) {
        console.error('Error cargando datos iniciales:', error);
        toastService.error('Error al cargar datos iniciales.');
      }
    };

    if (normalizedCita && empresaActual?.id) {
      cargarDatosIniciales();
    }
  }, [normalizedCita?.id, empresaActual?.id]);

  // Validaciones del formulario
  const validarFormulario = () => {
    //console.log('=== Validando formulario ===');
    //console.log('Form Data:', JSON.parse(JSON.stringify(formData)));
    
    let isValid = true;
    const newErrors = {};

    // Validar kilómetros
    if (!formData.kilometrajeActual || isNaN(parseInt(formData.kilometrajeActual))) {
      newErrors.kilometrajeActual = 'El kilometraje es requerido y debe ser un número válido';
      isValid = false;
    }

    // Validar al menos un mecánico seleccionado
    if (formData.mecanicosIds.length === 0) {
      newErrors.mecanicosIds = 'Debe seleccionar al menos un mecánico';
      isValid = false;
    }

    // Validar al menos un servicio realizado
    if (formData.serviciosRealizados.length === 0) {
      newErrors.serviciosRealizados = 'Debe agregar al menos un servicio realizado';
      isValid = false;
    } else {
      // Validar cada servicio
      formData.serviciosRealizados.forEach((servicio, index) => {
        if (!servicio.servicioId) {
          newErrors[`servicios.${index}.servicioId`] = 'Seleccione un servicio';
          isValid = false;
        }
        if (!servicio.precio || isNaN(parseFloat(servicio.precio))) {
          newErrors[`servicios.${index}.precio`] = 'Precio inválido';
          isValid = false;
        }
      });
    }

    // Validar repuestos
    if (formData.repuestosUtilizados && formData.repuestosUtilizados.length > 0) {
      formData.repuestosUtilizados.forEach((repuesto, index) => {
        if (!repuesto.repuestoId) {
          newErrors[`repuestos.${index}.repuestoId`] = { message: 'Seleccione un repuesto' };
          isValid = false;
        }
        if (!repuesto.cantidad || isNaN(parseInt(repuesto.cantidad)) || parseInt(repuesto.cantidad) < 1) {
          newErrors[`repuestos.${index}.cantidad`] = { message: 'Cantidad inválida' };
          isValid = false;
        }
        if (repuesto.precioUnitario === undefined || repuesto.precioUnitario === '' || isNaN(parseFloat(repuesto.precioUnitario)) || parseFloat(repuesto.precioUnitario) < 0) {
          newErrors[`repuestos.${index}.precioUnitario`] = { message: 'Precio unitario inválido' };
          isValid = false;
        }
      });
    }

    // Validar ítems adicionales
    formData.itemsAdicionales.forEach((item, index) => {
      if (!item.descripcion) {
        newErrors[`itemsAdicionales[${index}].descripcion`] = { message: 'Descripción requerida' };
        isValid = false;
      }
      if (!item.cantidad || isNaN(parseInt(item.cantidad)) || parseInt(item.cantidad) < 1) {
        newErrors[`itemsAdicionales[${index}].cantidad`] = { message: 'Cantidad inválida' };
        isValid = false;
      }
      if (item.precio_unitario === undefined || item.precio_unitario === '' || isNaN(parseFloat(item.precio_unitario))) {
        newErrors[`itemsAdicionales[${index}].precio_unitario`] = { message: 'Precio unitario inválido' };
        isValid = false;
      }
    });

    //console.log('Errores encontrados:', newErrors);
    //console.log('¿El formulario es válido?', isValid);
    //console.log('==============================');
    
    setErrors(newErrors);
    return isValid;
  };

  // Manejadores de cambios individuales
  const handleServicioChange = (index, field, value) => {
    const updatedServicios = [...formData.serviciosRealizados];
    
    if (field === 'servicioId') {
      const servicioSeleccionado = servicios.find(s => s.id === value);
      if (servicioSeleccionado) {
        // El servicio ya está normalizado desde la carga inicial
        updatedServicios[index] = {
          ...updatedServicios[index],
          servicioId: value,
          precio: (servicioSeleccionado.precio || servicioSeleccionado.Precio || 0).toString(),
          nombre: servicioSeleccionado.nombre || servicioSeleccionado.Nombre || '',
          descripcion: servicioSeleccionado.descripcion || servicioSeleccionado.Descripcion || ''
        };
      } else {
        updatedServicios[index] = {
          ...updatedServicios[index],
          servicioId: value,
          nombre: '',
          descripcion: ''
        };
      }
    } else {
      updatedServicios[index] = {
        ...updatedServicios[index],
        [field]: value
      };
    }
    
    setFormData(prev => ({ ...prev, serviciosRealizados: updatedServicios }));
  };

  const handleRepuestoChange = (index, field, value) => {
    const updatedRepuestos = [...formData.repuestosUtilizados];
    
    if (field === 'repuestoId') {
      const repuestoSeleccionado = repuestos.find(r => r.id === value);
      if (repuestoSeleccionado) {
        // El repuesto ya está normalizado desde la carga inicial
        updatedRepuestos[index] = {
          ...updatedRepuestos[index],
          repuestoId: value,
          precioUnitario: (repuestoSeleccionado.precio || repuestoSeleccionado.Precio || 0).toString(),
          nombre: repuestoSeleccionado.nombre || repuestoSeleccionado.Nombre || '',
          codigo: repuestoSeleccionado.codigo || repuestoSeleccionado.Codigo || '',
          stockDisponible: repuestoSeleccionado.stock || repuestoSeleccionado.Stock || 0
        };
      } else {
        updatedRepuestos[index] = {
          ...updatedRepuestos[index],
          repuestoId: value,
          nombre: '',
          codigo: '',
          stockDisponible: 0
        };
      }
    } else {
      updatedRepuestos[index] = {
        ...updatedRepuestos[index],
        [field]: value
      };
    }
    
    setFormData(prev => ({ ...prev, repuestosUtilizados: updatedRepuestos }));
  };

  const handleItemAdicionalChange = (index, field, value) => {
    const updatedItems = [...formData.itemsAdicionales];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    setFormData(prev => ({ ...prev, itemsAdicionales: updatedItems }));
  };

  // Manejadores para agregar elementos
  const handleAddServicio = () => {
    setFormData(prev => ({
      ...prev,
      serviciosRealizados: [...prev.serviciosRealizados, {
        servicioId: '',
        precio: '',
        observaciones: '',
        esServicioCita: false,
        recordatorioActivo: false,
        tipoRecordatorio: 'kilometraje',
        proximoKilometraje: '',
        proximaFecha: '',
        kilometrosAnticipacion: 1000,
        diasAnticipacion: 30,
        descripcionProximoServicio: '',
        mecanicoObservaciones: ''
      }]
    }));
  };

  const handleAddRepuesto = () => {
    setFormData(prev => ({
      ...prev,
      repuestosUtilizados: [...prev.repuestosUtilizados, {
        repuestoId: '',
        cantidad: 1,
        precioUnitario: '',
        observaciones: '',
        nombre: '',
        codigo: '',
        stockDisponible: 0
      }]
    }));
  };

  const handleAddItemAdicional = () => {
    setFormData(prev => ({
      ...prev,
      itemsAdicionales: [...prev.itemsAdicionales, { 
        descripcion: '', 
        cantidad: 1, 
        precio_unitario: '',
        observaciones: ''
      }]
    }));
  };

  // Manejadores para remover elementos
  const handleRemoveServicio = (index) => {
    setFormData(prev => ({
      ...prev,
      serviciosRealizados: prev.serviciosRealizados.filter((_, i) => i !== index)
    }));
  };

  const handleRemoveRepuesto = (index) => {
    setFormData(prev => ({
      ...prev,
      repuestosUtilizados: prev.repuestosUtilizados.filter((_, i) => i !== index)
    }));
  };

  const handleRemoveItemAdicional = (index) => {
    setFormData(prev => ({
      ...prev,
      itemsAdicionales: prev.itemsAdicionales.filter((_, i) => i !== index)
    }));
  };

  // Función para enviar el formulario
  const handleSubmit = async (event = null, options = {}) => {
    const { skipImageUpload = false, skipPDFGeneration = false } = options;
    //console.log('=== Iniciando handleSubmit ===');
    const formIsValid = validarFormulario();
    //console.log('¿Formulario válido?', formIsValid);
    //console.log('Errores actuales:', errors);
    
    if (!formIsValid) {
      //console.log('Formulario inválido, deteniendo envío');
      toastService.error('Por favor corrija los errores en el formulario');
      return false;
    }
    
    //console.log('Formulario válido, continuando...');

    setIsSubmitting(true);
    
    try {
      // Verificar que tenemos empresa actual
      if (!empresaActual?.id) {
        throw new Error('No hay empresa seleccionada');
      }
      
      // Debug de la cita normalizada
      console.log('🔍 Datos de la cita normalizada:', {
        id: normalizedCita.id,
        patente: normalizedCita.patente,
        vehiculo_id: normalizedCita.vehiculo_id,
        sucursal_id: normalizedCita.sucursal_id,
        vehiculo: normalizedCita.vehiculo
      });
      
      // Preparar datos para mantenimiento completo
      // Mapear campos de la estructura real de cita
      // Nota: empresa_id se determina através de sucursal_id en el backend
      const mantenimientoData = {
        cita_id: normalizedCita.id || normalizedCita.ID,
        vehiculo_id: normalizedCita.vehiculo_id || normalizedCita.VehiculoID,
        patente: normalizedCita.patente || normalizedCita.Patente, // Incluir la patente directamente
        sucursal_id: normalizedCita.sucursal_id || normalizedCita.SucursalID,
        empresa_id: empresaActual.id, // ✅ Incluir empresa_id para validación en backend
        mecanicos_ids: formData.mecanicosIds,
        kilometraje_actual: parseInt(formData.kilometrajeActual),
        observaciones: formData.observaciones,
        iva_porcentaje: ivaPorcentaje,
        servicios: formData.serviciosRealizados.map(servicio => ({
          servicio_id: servicio.servicioId,
          precio: parseFloat(servicio.precio),
          observaciones: servicio.observaciones,
          recordatorio_activo: servicio.recordatorioActivo,
          tipo_recordatorio: servicio.recordatorioActivo ? servicio.tipoRecordatorio : null,
          proximo_mantenimiento_kilometraje: servicio.recordatorioActivo && 
            (servicio.tipoRecordatorio === 'kilometraje' || servicio.tipoRecordatorio === 'ambos')
              ? parseInt(servicio.proximoKilometraje)
              : null,
          proximo_mantenimiento_fecha: servicio.recordatorioActivo && 
            (servicio.tipoRecordatorio === 'fecha' || servicio.tipoRecordatorio === 'ambos')
              ? servicio.proximaFecha
              : null,
          descripcion_proximo_servicio: servicio.recordatorioActivo 
            ? servicio.descripcionProximoServicio 
            : null,
          mecanico_observaciones: servicio.mecanicoObservaciones || null
        })),
        repuestos: formData.repuestosUtilizados.map(repuesto => ({
          repuesto_id: repuesto.repuestoId,
          cantidad: parseInt(repuesto.cantidad),
          precio_unitario: parseFloat(repuesto.precioUnitario),
          observaciones: repuesto.observaciones || ''
        })),
        items_adicionales: formData.itemsAdicionales.map(item => ({
          descripcion: item.descripcion,
          cantidad: parseInt(item.cantidad) || 1,
          precio_unitario: parseFloat(item.precio_unitario || 0),
          observaciones: item.observaciones || ''
        }))
      };

      //console.log('=== Enviando datos al API ===');
      //console.log('mantenimientoData:', JSON.stringify(mantenimientoData, null, 2));

      // Crear mantenimiento completo (usa transacción)
      const mantenimiento = await mantenimientosApiService.createMantenimientoCompleto(mantenimientoData);

      // Actualizar kilometraje del vehículo usando API multi-empresa
      const patente = normalizedCita.patente || normalizedCita.Patente || normalizedCita.vehiculo?.patente;
      console.log('🚗 Patente para actualizar kilometraje:', patente);
      if (patente) {
        try {
          await vehiculosApiService.updateKilometraje(patente, parseInt(formData.kilometrajeActual));
          console.log('✅ Kilometraje del vehículo actualizado correctamente');
        } catch (error) {
          console.warn('Error actualizando kilometraje del vehículo:', error);
          // No fallar el flujo principal si hay error actualizando kilometraje
        }
      }

      // Marcar cita como finalizada
      if (citasApiService.updateCita) {
        const citaId = normalizedCita.id || normalizedCita.ID;
        if (citaId) {
          await citasApiService.updateCita(citaId, { 
            estado_id: 4, // ID del estado "Completada"
            finalizada: true 
          });
        }
      }

      // Generar PDF de garantía y guardarlo en BD (solo si no se solicita saltarlo)
      if (!skipPDFGeneration) {
        console.log('=== Iniciando generación de PDF ===');
        const pdfResult = await generarPDFGarantia(mantenimiento);
        console.log('PDF generado:', pdfResult ? 'Éxito' : 'Error', pdfResult);
        
        // Guardar PDF base64 en la base de datos
        if (pdfResult?.base64) {
          try {
            console.log('Guardando PDF base64 en BD...');
            await mantenimientosApiService.guardarPDFBase64(mantenimiento.id, pdfResult.base64);
            console.log('PDF base64 guardado en la base de datos exitosamente');
          } catch (error) {
            console.error('Error guardando PDF base64:', error);
            toastService.error('Error al guardar el PDF de garantía en la base de datos');
            // Continuar pero notificar el error al usuario
          }
        } else {
          console.warn('No se recibió PDF base64 para guardar:', pdfResult);
          toastService.warning('PDF generado pero no se pudo guardar en la base de datos');
        }
      }

      // Mostrar mensaje de éxito
      toastService.success('Mantenimiento finalizado correctamente');
      
      // Llamar a la función de éxito
      if (onSuccess) onSuccess();
      
      // Si se solicita skipImageUpload, retornar el objeto mantenimiento para subir imágenes después
      if (skipImageUpload) {
        return mantenimiento;
      }
      
      return true;
    } catch (error) {
      console.error('Error al finalizar mantenimiento:', error);
      toastService.error('Error al finalizar el mantenimiento');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para generar PDF de garantía
  const handleGeneratePDF = async (mantenimientoCreado = null) => {
    try {
      // Obtener configuración del taller específica de la empresa
      const configTaller = await mantenimientosApiService.getConfiguracionTaller(empresaActual?.id);
      
      // Crear instancia del generador de PDF
      const pdfGenerator = new PDFGenerator();
      pdfGenerator.setConfigTaller(configTaller);
      
      // Obtener nombres de mecánicos seleccionados
      const mecanicosSeleccionados = mecanicos.filter(m => 
        formData.mecanicosIds.includes(String(m.id))
      );

      // Obtener todas las imágenes (de la cita y del mantenimiento si existe)
      let imagenesAntes = [];
      let imagenesDespues = [];
      
      try {
        // Siempre obtener imágenes de la cita (estado previo)
        const citaId = normalizedCita.id || normalizedCita.ID;
        if (citaId) {
          const CitaImagenesApiService = (await import('../services/citaImagenesApiService')).default;
          imagenesAntes = await CitaImagenesApiService.obtenerImagenesCita(session, citaId);
          console.log(`📸 Obtenidas ${imagenesAntes.length} imágenes de la cita para PDF`);
        }

        // Si hay un mantenimiento creado, obtener sus imágenes posteriores
        if (mantenimientoCreado?.id) {
          const { obtenerImagenesMantenimiento } = await import('../services/mantenimientoImagenesApiService');
          imagenesDespues = await obtenerImagenesMantenimiento(mantenimientoCreado.id, 'despues');
          console.log(`📸 Obtenidas ${imagenesDespues.length} imágenes posteriores del mantenimiento para PDF`);
        }
      } catch (error) {
        console.warn('Error obteniendo imágenes para PDF:', error);
        // Continuar sin imágenes si hay error
      }

      // Preparar datos para el PDF
      const datosMantenimiento = {
        vehiculo: normalizedCita?.vehiculo || {},
        cliente: normalizedCita?.cliente || {},
        kilometrajeActual: formData.kilometrajeActual,
        observaciones: formData.observaciones,
        ivaPorcentaje: ivaPorcentaje,
        servicios: formData.serviciosRealizados,
        repuestos: formData.repuestosUtilizados,
        itemsAdicionales: formData.itemsAdicionales,
        mecanicos: mecanicosSeleccionados,
        // Incluir todas las imágenes
        imagenesAntes: imagenesAntes,
        imagenesDespues: imagenesDespues
      };
      
      // Generar PDF y devolver resultado
      const pdfResult = await pdfGenerator.generarPDFGarantia(datosMantenimiento);
      return pdfResult;
    } catch (error) {
      console.error('Error generando PDF:', error);
      toastService.error('Error al generar el PDF de garantía');
      throw error;
    }
  };

  // Función para generar PDF de garantía interno
  const generarPDFGarantia = async (mantenimiento) => {
    try {
      //console.log('Generando PDF de garantía para mantenimiento:', mantenimiento.id);
      // Llamar a la función de generación de PDF pasando el mantenimiento creado
      const pdfResult = await handleGeneratePDF(mantenimiento);
      return pdfResult;
    } catch (error) {
      console.error('Error generando PDF de garantía:', error);
      // No fallamos el flujo principal si hay error generando el PDF
      return null;
    }
  };

  // Función para limpiar el formulario (memoizada para evitar loops)
  const resetFormData = useCallback(() => {
    setFormData({
      mecanicosIds: [],
      kilometrajeActual: '',
      observaciones: '',
      serviciosRealizados: [],
      repuestosUtilizados: [],
      itemsAdicionales: []
    });
    setErrors({});
  }, []);

  return {
    formData,
    setFormData,
    servicios,
    repuestos,
    mecanicos,
    ivaPorcentaje,
    isSubmitting,
    errors,
    handleServicioChange,
    handleRepuestoChange,
    handleItemAdicionalChange,
    handleAddServicio,
    handleAddRepuesto,
    handleAddItemAdicional,
    handleRemoveServicio,
    handleRemoveRepuesto,
    handleRemoveItemAdicional,
    handleSubmit,
    handleGeneratePDF,
    resetFormData
  };
};

export default useFinalizarMantenimiento;
